import { action, computed, makeObservable, runInAction } from "mobx";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbInfiniteScrollGridViewStore } from "mrb/components/infinite-scroll-grid";
import { MrbModalParams } from "mrb/common/models";
import { MrbSimpleSelectStore, MrbQuerySimpleSelectStore } from "mrb/components/select";
import { HomeGridItemDecorator } from "application/home/models";
import { HomeContentFilter } from "application/home/filters";
import { podcastType, podcastContentType } from "common/constants";
import { UpdatePodcastStatusOptions, UpdateStationStatusOptions } from "application/common/models";

import { isNil, forEach, findIndex, map, filter } from "lodash";

import {
    CategoryManageSelectedItemsViewStore,
    CategoryBulkSelectViewStore,
    CategoryGenericSearchViewStore,
} from "application/categories/stores";

class BaseCategoryViewStore extends MrbBaseListViewStore {
    @computed get selectedDevice() {
        return this.rootStore.userDeviceStore.selectedDevice;
    }

    @computed get anyDevice() {
        return this.rootStore.userDeviceStore.devices.length > 0;
    }

    @computed get inSearchMode() {
        return this.categoryGenericSearchViewStore.showSearchView;
    }

    @computed get selectedPremiumDisabledOption() {
        if (!isNil(this.queryUtility.filter.isEnabled)) {
            return this.queryUtility.filter.isEnabled ? 1 : 2;
        }
        return 0;
    }

    category = null;
    contentType = null;

    premiumDisabledOptions = [
        {
            label: "APPLICATION.HOME.LIST.FILTER.ALL",
            value: 0,
        },
        {
            label: "APPLICATION.HOME.LIST.FILTER.ENABLED",
            value: 1,
        },
        {
            label: "APPLICATION.HOME.LIST.FILTER.DISABLED",
            value: 2,
        },
    ];

    orderOptions = [
        {
            label: "APPLICATION.NAKI_NIGUN.LIST.ORDER.ALPHABETICALY_OPTION",
            value: "title",
        },
        {
            label: "APPLICATION.NAKI_NIGUN.LIST.ORDER.RECENTLY_ADDED_OPTION",
            value: "dateCreated",
        },
    ];

    constructor(rootStore) {
        const pageSize = 5;
        super(rootStore, {
            queryConfig: {
                filter: new HomeContentFilter(),
                disableUpdateQueryParams: true,
                orderBy: "title",
                orderDirection: "asc",
                pageSize: pageSize,
                queryParamMap: {
                    page: {
                        toQuery: () => 1,
                    },
                },
            },
        });
        makeObservable(this);
        this.selectedItemsViewStore = new CategoryManageSelectedItemsViewStore(
            this.rootStore,
            new CategoryBulkSelectViewStore(this.rootStore, this)
        );
        this.categoryGenericSearchViewStore = new CategoryGenericSearchViewStore(this.rootStore);
        this.connectDeviceModal = new MrbModalParams(
            {
                onClose: () => this.rootStore.userStore.disableConnectDeviceMessage(),
            },
            {
                goToConnectDevice: this.goToConnectDevice,
            }
        );

        this.setGridStore(
            new MrbInfiniteScrollGridViewStore(this.rootStore, this.queryUtility, {
                onCellClick: (item) => this.onClickCellItem(item),
                generateKey: (item) => item.podcastId || item.stationId,
                mapper: (item) => {
                    item.homeContents = map(
                        item.homeContents,
                        (content) =>
                            new HomeGridItemDecorator({
                                ...content,
                                coverImageUrl: this.createCoverImageUrl(content),
                            })
                    );

                    return item;
                },
            })
        );

        this.orderBySelectStore = new MrbQuerySimpleSelectStore(
            {
                textKey: "label",
                valueKey: "value",
                actions: {
                    onChange: (selectedOrder) => {
                        if (selectedOrder.value === "title" || selectedOrder.value === "name") {
                            this.queryUtility.filter.orderDirection = "asc";
                        } else {
                            this.queryUtility.filter.orderDirection = "desc";
                        }
                    },
                },
            },
            this.queryUtility,
            "orderBy",
            this.orderOptions
        );

        this.deviceSelectStore = new MrbSimpleSelectStore(
            {
                textKey: "deviceName",
                valueKey: "id",
                actions: {
                    onChange: (option) => {
                        this.rootStore.userDeviceStore.setSelectedDevice(option.item);
                    },
                },
            },
            this.rootStore.userDeviceStore.devices
        );
        this.deviceSelectStore.setSelectedItem(this.selectedDevice);

        this.reaction(
            () => this.selectedDevice,
            () => {
                if (!this.inSearchMode) {
                    this.queryUtility.fetch();
                }
            }
        );

        this.reaction(
            () => this.inSearchMode,
            (inSearchMode) => {
                if (!inSearchMode) {
                    this.queryUtility.fetch();
                }
            }
        );

        this.createCoverImageUrl = this.createCoverImageUrl.bind(this);
    }

    @action.bound
    changePremiumDisabledFilter(value) {
        if (value === 0) {
            this.queryUtility.filter.set("isEnabled", null);
            this.queryUtility.filter.set("isPremium", null);
        } else if (value === 1) {
            this.queryUtility.filter.set("isEnabled", true);
            this.queryUtility.filter.set("isPremium", null);
        } else if (value === 2) {
            this.queryUtility.filter.set("isEnabled", false);
            this.queryUtility.filter.set("isPremium", null);
        }
        this.queryUtility.fetch();
    }

    onInit() {
        if (!this.anyDevice && this.rootStore.userStore.isConnectDeviceMessageVisible) {
            this.connectDeviceModal.open();
        }
        return this.queryUtility.initialize();
    }

    find(params) {
        throw new Error("Method not implemented.");
    }

    async fetchResources(filter) {
        // Display grid (Skeleton loader) only during the first page load, and in other cases, during loading more data on scroll event,
        // display only top bar loader (mainLoaderStore)
        const isFirstPageLoad = filter.pageNumber === 1;
        if (isFirstPageLoad) {
            this.gridStore.suspend();
        } else {
            this.rootStore.mainLoaderStore.suspend();
        }
        try {
            const response = await this.find(filter);
            runInAction(() => {
                this.gridStore.setData(response);
                if (isFirstPageLoad) {
                    this.gridStore.resume();
                } else {
                    this.rootStore.mainLoaderStore.resume();
                }
            });
        } catch (err) {
            if (isFirstPageLoad) {
                this.gridStore.resume();
            } else {
                this.rootStore.mainLoaderStore.resume();
            }
            this.onFetchError(err);
        }
    }

    @action.bound
    onBulkEditSuccess(bulkEditResource) {
        forEach(this.gridStore.data, (data) => {
            if (data.homeContents) {
                forEach(data.homeContents, (item) => {
                    if (item.podcastId && item.podcastType === podcastType.podcast) {
                        const contentAccessResult = this.rootStore.contentAccessValidator.canAccessPodcast({
                            isPremium: item.isPremium,
                            unavailableInMobileApp: item.isVisibleInApp === false,
                            unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
                        });
                        if (contentAccessResult.canAccess) {
                            const selectedIndex = findIndex(
                                bulkEditResource.selectedPodcastIds,
                                (selectedId) => selectedId === item.podcastId
                            );
                            if (selectedIndex !== -1) {
                                item.isTurnedOn = true;
                                return;
                            }

                            const deselectedIndex = findIndex(
                                bulkEditResource.deselectedPodcastIds,
                                (deselectedId) => deselectedId === item.podcastId
                            );
                            if (deselectedIndex !== -1) {
                                item.isTurnedOn = false;
                                return;
                            }

                            if (!isNil(bulkEditResource.isAllSelected)) {
                                item.isTurnedOn = bulkEditResource.isAllSelected;
                            }
                        }
                    } else if (item.podcastId && item.podcastType === podcastType.podcastGroup) {
                        const contentAccessResult = this.rootStore.contentAccessValidator.canAccessPodcast({
                            isPremium: item.isPremium,
                            unavailableInMobileApp: item.isVisibleInApp === false,
                            unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
                        });
                        if (contentAccessResult.canAccess) {
                            const selectedIndex = findIndex(
                                bulkEditResource.selectedPodcastGroupIds,
                                (selectedId) => selectedId === item.podcastId
                            );
                            if (selectedIndex !== -1) {
                                item.numberOfEnabledSubPodcasts = item.numberOfSubPodcasts;
                                return;
                            }

                            const deselectedIndex = findIndex(
                                bulkEditResource.deselectedPodcastGroupIds,
                                (deselectedId) => deselectedId === item.podcastId
                            );
                            if (deselectedIndex !== -1) {
                                item.numberOfEnabledSubPodcasts = 0;
                                return;
                            }

                            if (!isNil(bulkEditResource.isAllSelected)) {
                                item.numberOfEnabledSubPodcasts = bulkEditResource.isAllSelected
                                    ? item.numberOfSubPodcasts
                                    : 0;
                            }
                        }
                    } else if (item.stationId) {
                        const contentAccessResult = this.rootStore.contentAccessValidator.canAccessStation({
                            isPremium: item.isPremium,
                            unavailableInMobileApp: item.isVisibleInApp === false,
                            unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
                        });
                        if (contentAccessResult.canAccess) {
                            const selectedIndex = findIndex(
                                bulkEditResource.selectedStationIds,
                                (selectedId) => selectedId === item.stationId
                            );
                            if (selectedIndex !== -1) {
                                item.isTurnedOn = true;
                                return;
                            }

                            const deselectedIndex = findIndex(
                                bulkEditResource.deselectedStationIds,
                                (deselectedId) => deselectedId === item.stationId
                            );
                            if (deselectedIndex !== -1) {
                                item.isTurnedOn = false;
                                return;
                            }

                            if (!isNil(bulkEditResource.isAllSelected)) {
                                item.isTurnedOn = bulkEditResource.isAllSelected;
                            }
                        }
                    }
                });
            }
        });
    }

    goTo = (item) => {
        if (item.stationId) {
            this.rootStore.routerStore.goTo("master.application.home.station", {
                id: item.stationId,
            });
        } else if (item.podcastId) {
            if (item.podcastType === podcastType.podcast) {
                if (item.podcastContentType === podcastContentType.torahAnytime) {
                    if (this.anyDevice) {
                        this.rootStore.routerStore.goTo("master.application.home.torah-anytime", {
                            id: item.podcastId,
                        });
                    }
                } else {
                    this.rootStore.routerStore.goTo("master.application.home.podcast", {
                        id: item.podcastId,
                    });
                }
            } else {
                this.rootStore.routerStore.goTo("master.application.home.podcast-group", {
                    id: item.podcastId,
                });
            }
        }
    };

    goToConnectDevice = () => {
        this.rootStore.routerStore.goTo("master.application.device.create");
    };

    onClickCellItem = async (item) => {
        if (item.podcastId && item.podcastType === podcastType.podcastGroup) {
            const contentAccessResult = this.rootStore.contentAccessValidator.canAccessPodcast({
                isPremium: item.isPremium,
                isLegacySubscriptionValid: item.validLegacySubscription,
                unavailableInMobileApp: item.isVisibleInApp === false,
                unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
            });
            if (contentAccessResult.canAccess) {
                await this.changeDevicePodcastGroupStatus(item);
            }
        } else if (item.podcastId && item.podcastContentType === podcastContentType.torahAnytime) {
            if (this.anyDevice) {
                this.rootStore.routerStore.goTo("master.application.home.torah-anytime", { id: item.podcastId });
            }
        } else {
            if (item.stationId) {
                const contentAccessResult = this.rootStore.contentAccessValidator.canAccessStation({
                    isPremium: item.isPremium,
                    isLegacySubscriptionValid: item.validLegacySubscription,
                    unavailableInMobileApp: item.isVisibleInApp === false,
                    unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
                });
                if (contentAccessResult.canAccess) {
                    await this.changeDeviceStationStatus(item);
                }
            } else if (item.podcastId) {
                const contentAccessResult = this.rootStore.contentAccessValidator.canAccessPodcast({
                    isPremium: item.isPremium,
                    isLegacySubscriptionValid: item.validLegacySubscription,
                    unavailableInMobileApp: item.isVisibleInApp === false,
                    unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
                });
                if (contentAccessResult.canAccess) {
                    await this.changeDevicePodcastStatus(item);
                }
            }
        }
    };

    async changeDeviceStationStatus(item) {
        const duplicateStations = [];
        forEach(this.gridStore.data, (x) => {
            if (x.homeContents) {
                duplicateStations.push(...filter(x.homeContents, (content) => content.stationId === item.stationId));
            }
        });
        if (!item.isUpdating) {
            try {
                forEach(duplicateStations, (station) => {
                    station.isUpdating = true;
                });
                this.rootStore.mainLoaderStore.suspend();
                const newValue = !item.isTurnedOn;
                const options = new UpdateStationStatusOptions(
                    item.stationId,
                    item.title,
                    item.isPremium,
                    item.isVisibleInApp === false
                );
                await this.rootStore.userContentManagerStore.updateStationStatus(options, newValue);
                runInAction(() => {
                    forEach(duplicateStations, (station) => {
                        station.isTurnedOn = newValue;
                    });
                });
            } catch (err) {
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Please contact the support team.",
                    err
                );
            } finally {
                forEach(duplicateStations, (station) => {
                    delete station.isUpdating;
                });
                this.rootStore.mainLoaderStore.resume();
            }
        }
    }

    async changeDevicePodcastStatus(item) {
        const duplicatePodcasts = [];
        forEach(this.gridStore.data, (x) => {
            if (x.homeContents) {
                duplicatePodcasts.push(...filter(x.homeContents, (content) => content.podcastId === item.podcastId));
            }
        });

        if (!item.isUpdating) {
            try {
                forEach(duplicatePodcasts, (podcast) => {
                    podcast.isUpdating = true;
                });

                this.rootStore.mainLoaderStore.suspend();

                const newValue = !item.isTurnedOn;
                const options = new UpdatePodcastStatusOptions(
                    item.podcastId,
                    item.title,
                    item.isPremium,
                    item.isVisibleInApp === false
                );

                await this.rootStore.userContentManagerStore.updatePodcastStatus(options, newValue);

                runInAction(() => {
                    forEach(duplicatePodcasts, (podcast) => {
                        podcast.isTurnedOn = newValue;
                    });
                });
            } catch (err) {
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Please contact the support team.",
                    err
                );
            } finally {
                forEach(duplicatePodcasts, (podcast) => {
                    delete podcast.isUpdating;
                });

                this.rootStore.mainLoaderStore.resume();
            }
        }
    }

    @action.bound
    async changeDevicePodcastGroupStatus(item) {
        const duplicatePodcasts = [];
        forEach(this.gridStore.data, (x) => {
            if (x.homeContents) {
                duplicatePodcasts.push(...filter(x.homeContents, (content) => content.podcastId === item.podcastId));
            }
        });

        if (!item.isUpdating) {
            try {
                forEach(duplicatePodcasts, (podcast) => {
                    podcast.isUpdating = true;
                });

                this.rootStore.mainLoaderStore.suspend();

                const newValue = !(item.numberOfEnabledSubPodcasts === item.numberOfSubPodcasts);
                const options = new UpdatePodcastStatusOptions(
                    item.podcastId,
                    item.title,
                    item.isPremium,
                    item.isVisibleInApp === false
                );

                await this.rootStore.userContentManagerStore.updatePodcastGroupStatus(
                    options,
                    newValue,
                    this.contentType
                );

                runInAction(() => {
                    forEach(duplicatePodcasts, (podcast) => {
                        podcast.isTurnedOn = newValue;
                        podcast.numberOfEnabledSubPodcasts = newValue ? podcast.numberOfSubPodcasts : 0;
                    });
                });
            } catch (err) {
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Please contact the support team.",
                    err
                );
            } finally {
                forEach(duplicatePodcasts, (podcast) => {
                    delete podcast.isUpdating;
                });

                this.rootStore.mainLoaderStore.resume();
            }
        }
    }

    onFetchError(error) {
        this.rootStore.notificationStore.error(
            "Unexpected error occurred while trying to load content. Please contact the support team.",
            error
        );
    }

    createCoverImageUrl(item) {
        if (item.coverImageId) {
            if (item.stationId) {
                return this.rootStore.coverImageFactory.createStationCoverImageUrl(item.coverImageId, 200, 200);
            } else if (item.podcastId) {
                return this.rootStore.coverImageFactory.createPodcastCoverImageUrl(item.coverImageId, 200, 200);
            }
        }
        return null;
    }

    createPodcastGroupCoverImageUrl(coverImageId) {
        if (coverImageId) {
            return this.rootStore.coverImageFactory.createPodcastCoverImageUrl(coverImageId, 200, 200);
        }
        return null;
    }

    onDestroy() {
        this.selectedItemsViewStore.destroy();
        this.categoryGenericSearchViewStore.destroy();
    }
}

export default BaseCategoryViewStore;
